import { useState } from 'react';

/**
 * Inline SVG brand mark — an outlined cloud split down the middle: a brain
 * silhouette on the left and a circuit/network half with an "AI" chip on
 * the right, mirroring the reference brand artwork. Transparent background
 * throughout. The brain ink color uses `var(--text)` so it automatically
 * switches between dark-on-light and light-on-dark depending on the active
 * theme; the cloud outline and circuit traces use the brand gradient, which
 * stays legible on both dark and light navbar backgrounds.
 */
export function CloudMark({ size = 40, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="braincloud"
    >
      <defs>
        <linearGradient id="bcStroke" x1="8" y1="10" x2="56" y2="50" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4f8cff" />
          <stop offset="1" stopColor="#a855f7" />
        </linearGradient>
      </defs>

      {/* cloud outline */}
      <g transform="translate(11, 11) scale(2.35)">
        <path
          d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"
          stroke="url(#bcStroke)"
          strokeWidth="1.05"
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* dashed divider between brain and circuit halves */}
      <path d="M32 21 V44" stroke="var(--text)" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2 2" />

      {/* brain silhouette — left hemisphere, theme-aware ink color */}
      <g stroke="var(--text)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none">
        <path d="M19.5 24.5C18.5 21 22 19 25 20C27 18.3 30 19 30.3 21.5C32 22.7 32 25 30.3 26C32 28 31 30.5 29 30.7C30.7 33 29.5 35.7 27.3 36C28 38.5 25.3 40.7 22.7 39.7C20.7 41.3 17.7 40 17.3 37.3C14.7 37 13.7 34 15.5 32.2C13.5 30.3 14.3 27.3 16.5 26.3C14.7 24.5 15.7 21.7 17.7 21C17.8 22.5 18.5 23.7 19.5 24.5Z" />
        <path d="M19.5 24.5C21.3 25.7 22 28 20.3 30.3" strokeWidth="1.1" opacity="0.55" />
        <path d="M16.5 26.3C19 27 20 29.7 18 32" strokeWidth="1.1" opacity="0.55" />
        <path d="M20.3 33.5C22.7 33 24.5 35 23 37.3" strokeWidth="1.1" opacity="0.55" />
      </g>

      {/* circuit traces — right hemisphere */}
      <g stroke="url(#bcStroke)" strokeWidth="1.3" strokeLinecap="round" fill="none">
        <path d="M38 27 H43.5 V22.5" />
        <path d="M38 32 H46" />
        <path d="M38 37 H43.5 V41.5" />
      </g>
      <g fill="none" stroke="url(#bcStroke)" strokeWidth="1.2">
        <circle cx="43.5" cy="21" r="1.6" />
        <circle cx="43.5" cy="43" r="1.6" />
      </g>
      <circle cx="47.5" cy="32" r="1.7" fill="#22d3ee" />

      {/* AI chip, centered on the divider */}
      <rect x="26.5" y="27" width="11" height="10" rx="2.4" fill="url(#bcStroke)" />
      <text
        x="32"
        y="33.6"
        textAnchor="middle"
        fontFamily="'Space Grotesk', 'Inter', sans-serif"
        fontSize="6.2"
        fontWeight="700"
        fill="#ffffff"
      >
        AI
      </text>
    </svg>
  );
}

/**
 * Logo: `variant="full"` renders the real raster artwork from
 * public/logo.png (drop your downloaded image there). If that file is
 * missing it gracefully falls back to the inline SVG mark. `variant="icon"`
 * always uses the compact SVG mark — ideal for the navbar.
 */
export default function Logo({ size = 40, variant = 'icon', className }) {
  const [err, setErr] = useState(false);

  if (variant === 'full' && !err) {
    return (
      <img
        src={process.env.PUBLIC_URL + '/logo.png'}
        alt="braincloud — Learn · Practice · Grow"
        className={className}
        onError={() => setErr(true)}
        style={{ height: size, width: 'auto', display: 'block' }}
      />
    );
  }

  return <CloudMark size={size} className={className} />;
}
